import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { TipsCard } from './TipsCard';
import { TipResponse } from '@/types/tips';

interface TipsListProps {
  tips: TipResponse[];
  isLoading: boolean;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onCreateAssignment: (tip: TipResponse) => void;
  onEdit: (tip: TipResponse) => void;
  onDelete: (id: string) => void;
}

export function TipsList({
  tips,
  isLoading,
  onApprove,
  onReject,
  onCreateAssignment,
  onEdit,
  onDelete,
}: TipsListProps) {
  return (
    <ScrollArea className='h-full'>
      <div className='p-4'>
        {isLoading && tips.length === 0 && (
          <div className='space-y-4'>
            {[1, 2, 3, 4, 5].map(i => (
              <Card key={i}>
                <CardHeader className='pb-3'>
                  <div className='flex items-start justify-between'>
                    <div className='flex-1 space-y-2'>
                      <div className='flex items-center gap-2'>
                        <Skeleton className='h-4 w-4' />
                        <Skeleton className='h-4 w-16' />
                        <Skeleton className='h-4 w-12' />
                        <Skeleton className='h-4 w-20' />
                      </div>
                      <Skeleton className='h-5 w-3/4' />
                      <Skeleton className='h-4 w-32' />
                    </div>
                    <div className='text-right'>
                      <Skeleton className='h-4 w-16 mb-1' />
                      <Skeleton className='h-6 w-12' />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Skeleton className='h-4 w-full mb-2' />
                  <Skeleton className='h-4 w-2/3 mb-4' />
                  <div className='flex gap-2'>
                    <Skeleton className='h-8 w-20' />
                    <Skeleton className='h-8 w-20' />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!isLoading && tips.length === 0 && (
          <div className='text-center py-12'>
            <Inbox className='h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50' />
            <div className='text-muted-foreground mb-4'>No tips available.</div>
            <Button variant='outline' onClick={() => onApprove}>
              Refresh Tips
            </Button>
          </div>
        )}

        <div className='space-y-4'>
          {tips.map(tip => (
            <TipsCard
              key={tip.id}
              tip={tip}
              onApprove={onApprove}
              onReject={onReject}
              onCreateAssignment={onCreateAssignment}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>

        {process.env.NODE_ENV === 'development' && (
          <div className='mt-8 p-4 bg-muted rounded-lg text-sm'>
            <p className='font-medium mb-2'>Debug Info:</p>
            <div className='grid grid-cols-2 gap-2 text-xs'>
              <p>All tips: {tips.length}</p>
              <p>Loading: {isLoading ? 'Yes' : 'No'}</p>
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
